import React, { useEffect, useState } from "react";
import "./jobs.scss";
import PreScreening from "../JobScreening/preScreening";
import TeleScreening from "../JobScreening/teleScreening";
import CandidateReportSharing from "../JobScreening/candidateReportSharing";
import VideoScreening from "../JobScreening/videoScreening";
import EvaluationFit from "../JobScreening/evaluationFit";
import TeamAssignment, { TEAM_ASSIGNMENT_TIPS } from "../JobScreening/teamAssignment";
import InfoTip from "../../utils/InfoTip";
import {
  fetchJobById,
  fetchQualifyingQuestions,
  fetchJobAssignmentSettings,
  fetchJobAccessList,
  fetchJobRoundRobinPool,
} from "../../Redux/ActionCreators/jobsAction";
import { useParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import { useSelector } from "react-redux";
import { Alert, Snackbar } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useAppPermission } from "../../hooks/useAppPermission";
import { P } from "../../constants/rbac";
import { JobSettingsAccessContext } from "./JobSettingsAccessContext";

const JobSettings = () => {
  const { jobId } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { can } = useAppPermission();
  const canEditSettings = can(P.JOBS_SETTINGS_EDIT) || can(P.JOBS_JOB_EDIT);
  const canViewSettings =
    can(P.JOBS_SETTINGS_READ) || can(P.JOBS_SETTINGS_EDIT) || can(P.JOBS_JOB_EDIT);
  const readOnly = canViewSettings && !canEditSettings;

  const [type, setType] = useState("");
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
  const [assignmentSummary, setAssignmentSummary] = useState(null);

  const job = useSelector((state) => state.jobs.latestJob);
  const questions = useSelector((state) => state.jobs.qualifyingQuestions || []);

  useEffect(() => {
    if (!canViewSettings) {
      navigate(jobId ? `/jobs/${jobId}/candidates` : "/jobs", { replace: true });
    }
  }, [canViewSettings, jobId, navigate]);

  const initialfetch = async () => {
    if (jobId) {
      setLoading(true);
      await dispatch(fetchJobById(jobId));
      await dispatch(fetchQualifyingQuestions(jobId));
      setLoading(false);
    }
  };

  const fetchAssignmentSummary = async () => {
    if (!jobId) return;
    const [settingsRes, aclRes, poolRes] = await Promise.all([
      dispatch(fetchJobAssignmentSettings(jobId)),
      dispatch(fetchJobAccessList(jobId)),
      dispatch(fetchJobRoundRobinPool(jobId)),
    ]);
    if (!settingsRes.success) {
      setAssignmentSummary(null);
      return;
    }
    const acl = Array.isArray(aclRes.data) ? aclRes.data : [];
    setAssignmentSummary({
      teamAccess: !!settingsRes.data?.team_access_enabled,
      roundRobin: !!settingsRes.data?.round_robin_enabled,
      teamCount: acl.filter((a) => a.is_active !== false).length,
      poolCount: Array.isArray(poolRes.data) ? poolRes.data.length : 0,
    });
  };

  useEffect(() => {
    initialfetch();
  }, [jobId]);

  // Refresh the card summary whenever we come back to the settings overview.
  useEffect(() => {
    if (type === "") fetchAssignmentSummary();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobId, type]);

  const hasPreScreeningStage = job?.hiring_stages?.some(
    (stage) => stage.hiring_stage_id === "3dab71cf-c680-4983-a109-1cff0953999b"
  );

  const videoStage = job?.hiring_stages?.find(
    (stage) =>
      stage.name?.toLowerCase().includes("video") ||
      stage.hiring_stage?.name?.toLowerCase().includes("video")
  );

  const teleStage = job?.hiring_stages?.find(
    (stage) => stage.hiring_stage_id === "fd40e3d2-38a9-4cef-937a-93ef6a42d045"
  );
  const isDynamicVoiceMode = teleStage?.data?.telephonic_call_mode === "DYNAMIC_VOICE_CALL";

  const openSection = (section) => setType(section);

  const actionIcon = (section) => (
    <span
      className="edit-icon"
      onClick={() => openSection(section)}
      title={readOnly ? "View" : "Edit"}
    >
      <img
        src={readOnly ? "/images/eye-black-icon.svg" : "/images/square-pen-black.svg"}
        alt={readOnly ? "View" : "Edit"}
        onError={(e) => {
          e.currentTarget.src = "/images/square-pen-black.svg";
        }}
      />
    </span>
  );

  return (
    <JobSettingsAccessContext.Provider value={{ readOnly, canEdit: canEditSettings }}>
      <div className={`job-settings${readOnly ? " job-settings--readonly" : ""}`}>
        {readOnly && type === "" && (
          <div style={{ marginBottom: 12, fontSize: 12, color: "#8a9199", fontWeight: 600 }}>
            View only — you can browse settings but cannot save changes.
          </div>
        )}
        {type === "" && (
          <div className="screening">
            {loading ? (
              <div
                className="loading-overlay"
                style={{
                  position: "relative",
                  marginTop: 16,
                  alignItems: "start",
                  height: "32px",
                }}
              >
                <div className="spinner-border" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
              </div>
            ) : (
              <>
                {hasPreScreeningStage && (
                  <div className="screening-option">
                    <div>
                      <h4>Pre-Screening</h4>
                      <div style={{ height: "40px" }}>
                        <p>{questions.length} questions</p>
                        <p>
                          Passing score:{" "}
                          {job?.hiring_stages?.find(
                            (stage) =>
                              stage.hiring_stage_id === "fd40e3d2-38a9-4cef-937a-93ef6a42d045"
                          )?.data?.passing_score ?? "N/A"}
                        </p>
                      </div>
                    </div>
                    {actionIcon("prescreening")}
                  </div>
                )}
                <div className="screening-option">
                  <div>
                    <h4>Telephonic Screening</h4>
                    <p style={{ height: "40px" }}>
                      {isDynamicVoiceMode
                        ? "Dynamic Voice Assistant"
                        : teleStage?.data?.voice_call_name ||
                          "Mounica  Voice Assistant (US Voice)"}
                    </p>
                  </div>
                  {actionIcon("telephone")}
                </div>
                <div className="screening-option">
                  <div>
                    <h4>Candidate Settings</h4>
                  </div>
                  {actionIcon("reportSharing")}
                </div>
                <div className="screening-option">
                  <div>
                    <h4>Video Interview Settings</h4>
                  </div>
                  {actionIcon("VideoScreening")}
                </div>
                <div className="screening-option">
                  <div>
                    <h4>Job Variants</h4>
                  </div>
                  {actionIcon("evaluationFit")}
                </div>
                <div className="screening-option">
                  <div>
                    <h4 style={{ display: "flex", alignItems: "center" }}>
                      Team &amp; Assignment
                      <InfoTip
                        text={`${TEAM_ASSIGNMENT_TIPS.teamAccess}\n\n${TEAM_ASSIGNMENT_TIPS.roundRobin}`}
                      />
                    </h4>
                    <div style={{ height: "40px" }}>
                      {assignmentSummary ? (
                        <>
                          <p>
                            Team access {assignmentSummary.teamAccess ? "ON" : "OFF"} · Round-robin{" "}
                            {assignmentSummary.roundRobin ? "ON" : "OFF"}
                          </p>
                          <p>
                            {assignmentSummary.teamCount} team member
                            {assignmentSummary.teamCount === 1 ? "" : "s"}
                            {assignmentSummary.roundRobin &&
                              ` · RR pool: ${assignmentSummary.poolCount || "empty"}`}
                          </p>
                        </>
                      ) : (
                        <p>Control who can see and work on candidates</p>
                      )}
                    </div>
                  </div>
                  {actionIcon("teamAssignment")}
                </div>
              </>
            )}
          </div>
        )}
        {type === "prescreening" && <PreScreening setType={setType} />}
        {type === "telephone" && <TeleScreening setType={setType} />}
        {type === "evaluationFit" && <EvaluationFit setType={setType} />}
        {type === "teamAssignment" && (
          <TeamAssignment setType={setType} setSnackbar={setSnackbar} />
        )}
        {type === "reportSharing" && (
          <CandidateReportSharing setType={setType} setSnackbar={setSnackbar} />
        )}
        {type === "VideoScreening" && (
          <VideoScreening
            setType={setType}
            setSnackbar={setSnackbar}
            jobHiringStageId={videoStage?.id}
            hiringStageId={videoStage?.hiring_stage_id}
          />
        )}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={3000}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        >
          <Alert
            onClose={() => setSnackbar({ ...snackbar, open: false })}
            severity={snackbar.severity}
            sx={{ width: "100%" }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </div>
    </JobSettingsAccessContext.Provider>
  );
};

export default JobSettings;
